/**
 * Hunyuan Video 1.5 descriptor (Phase 1, unit U5).
 *
 * PURE: `import type` + sibling data only (purity.test). Importable from server/ and src/.
 *
 * Reproduces today's behaviour (U0 goldens, families/hunyuan.parity.test.ts):
 *   editor   server/routes/falai.ts case 'hunyuan-1.5' :1727-1738 ({ prompt, num_frames: 121, image_url? }),
 *            billed-tier guard :1880-1900 (reserve, then 500 + refund above the 1x ladder tier)
 *   headless server/services/ProviderService.ts :1506-1513 ({ prompt, num_frames: 121, aspect_ratio, image_url? })
 *   price    server/services/CreditService.ts :1452-1454 (35 cr/s × the fixed 5 s = 175, whatever was asked)
 *
 * Schemas: __schemas__/fal-ai__hunyuan-video-v1.5__{text,image}-to-video.json (oracle 2026-09-19):
 * no `duration` (length = num_frames, default and max 121), `resolution` is const "480p".
 */
import type { ModelDescriptor } from '../types';
import { IMG, W_IMG } from '../_shared';
import { aboveOneXLadder } from './_u5Resolutions';

/**
 * The editor's post-reserve refusal (falai.ts:1880-1900: "charged at the X tier, but the payload
 * carries no resolution field" — it reserves, throws 500, refunds) fires for any resolution the
 * SHARED ladder prices above 1x. The descriptor refuses BEFORE reserving instead, which leaves
 * the same balance and makes no fal call (class B1).
 *
 * REVIEW ROUND 1, defects 2 and 4:
 *  - the list was 8 hand-written spellings; it is now DERIVED by running the shared
 *    `legacyResolutionMultiplier` (models/pricing.ts, the same function falai.ts's guard uses)
 *    over the verified spelling corpus, so descriptor and route share one rule rather than one
 *    rule and one transcription. It now covers '1920x1080', '3840x2160', '2160p60', ' 4k',
 *    '1080', '1024px', '2048px' and the rest of the corpus, which the 8-entry list did not.
 *  - the out-of-domain marker is still a non-string (the frozen vocabulary has no reject-rule on
 *    a param), but headless now COERCES to the default instead of passing it through, so the
 *    resolved value is always a string on every surface — a later PR that gives this param a
 *    wire field or a ladder rate cannot ship or price `false`. hunyuan.parity.test.ts asserts it.
 *
 * Every other string (garbage, '2k', '768P', ' 720p ') is priced at 1x and runs, as today.
 * Headless has no such guard: it keeps running them (per-surface `invalid`).
 *
 * RESIDUAL (escalated, see the U5 report): a >1x spelling OUTSIDE the corpus still runs on the
 * editor where legacy refused. Closing it needs a ladder-derived reject rule in types.ts, which
 * this unit may not edit; the residual is enumerated and pinned by hunyuan.parity.test.ts.
 */
const ABOVE_1X_LADDER: readonly string[] = aboveOneXLadder();

export const HUNYUAN_15: ModelDescriptor = {
  id: 'hunyuan-1.5', provider: 'falai', face: 'video', displayName: 'Hunyuan 1.5', ledgerName: 'Hunyuan 1.5', status: 'hidden',
  prompt: { field: 'prompt', required: true },
  modes: {
    t2v: { endpoint: 'fal-ai/hunyuan-video-v1.5/text-to-video', ports: [], media: {} },
    i2v: { endpoint: 'fal-ai/hunyuan-video-v1.5/image-to-video', ports: [IMG()], media: { image: W_IMG } },
  },
  params: [
    { id: 'resolution', type: 'string', default: '720p',
      aliases: Object.fromEntries(ABOVE_1X_LADDER.map((r) => [r, false])),
      invalid: { editor: 'reject', headless: { coerce: { kind: 'default' } } }, wire: false,
      ui: { label: 'Resolution', tier: 'hidden' } },
    { id: 'aspectRatio', type: 'enum', values: ['16:9', '9:16'], default: '16:9',
      invalid: { coerce: { kind: 'default' } }, wire: false,
      ui: { label: 'Aspect ratio', tier: 'basic' } },
  ],
  fixed: { num_frames: 121 },
  legacy: {
    wire: [
      { field: 'aspect_ratio', fromParam: 'aspectRatio', surface: 'headless',
        why: "headless clamps and sends aspect_ratio (ProviderService.ts:1512); the editor never sends it, so an editor clip renders at fal's 16:9 default whatever the flow says",
        retire: 'owner-approval' },
    ],
    omits: [{ field: 'resolution', why: 'schema const "480p"; nothing to send' }],
    notes: [
      'Duration is not a param: the schema has none and billing is pinned to the fixed 5 s. The editor ledger still records the requested seconds in its description ("Video: Hunyuan 1.5 (3s)") while charging 175.',
    ],
  },
  pricing: {
    unit: 'second', units: { fixed: 5 }, rounding: 'table-min1', policy: 'fal-video-35',
    skus: [{ id: 'hunyuan-1.5', rate: { flat: 35 },
      vendor: { usd: 0.075, per: 'second', field: 'pricingInfoOverride',
                text: 'Current pricing is 0.075 cents/s of video, more resolutions arriving soon.',
                source: 'fal-ai/hunyuan-video-v1.5/text-to-video (fal writes "cents"; it means $0.075/s)', verified: '2026-09-19' },
      registry: { displayName: 'Hunyuan 1.5', operationType: 'video', flatRate: false } }],
  },
  knowledge: { summary: 'Hunyuan Video 1.5: fixed ~5 s (121 frames) at 480p; billed 5 s always.' },
  schemaVerified: '2026-09-19',
};

export const HUNYUAN_MODELS: readonly ModelDescriptor[] = [HUNYUAN_15];
